import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  CalendarCheck,
  Clock,
  Check,
  X,
  Pencil,
  Video,
  Loader2,
} from 'lucide-react';
import { getMyAccountSnapshot } from '../services/firestoreService';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import PortalBackground from './PortalBackground';
import {
  fetchVisitWindows,
  fetchVisitSeatRequests,
  fetchUserVisitRequest,
  fetchVisitItinerary,
  submitVisitRequest,
  updateVisitRequest,
  deleteVisitRequest,
  computeAvailableWindows,
  formatWindowRange,
} from '../services/visitSchedulingService';

/**
 * Shared, redesigned booking page for the 2-day On-Site and CQC visits.
 * Content (heading, location, overview cards, video captions) is passed in;
 * scheduling logic + itinerary come from the shared visitSchedulingService.
 */
const VisitBookingPage = ({
  type, // 'onsite' | 'cqc'
  title,
  subtitle,
  location, // optional string
  accent, // 'green' | 'red'
  overviewTitle = 'What to Expect',
  overviewCards = [],
  videos = [], // [{ url, caption }, { url, caption }]
}) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [itinerary, setItinerary] = useState([]);
  const [availableWindows, setAvailableWindows] = useState([]);
  const [userRequest, setUserRequest] = useState(null);
  const [account, setAccount] = useState(null);

  const [selectedWindowId, setSelectedWindowId] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const accentClasses = {
    green: {
      solid: 'bg-emerald-600 hover:bg-emerald-700',
      ring: 'ring-emerald-500 border-emerald-500',
      tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      text: 'text-emerald-600 dark:text-emerald-400',
      chip: 'bg-emerald-500',
    },
    red: {
      solid: 'bg-red-600 hover:bg-red-700',
      ring: 'ring-red-500 border-red-500',
      tile: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      text: 'text-red-600 dark:text-red-400',
      chip: 'bg-red-500',
    },
  }[accent] || {};

  const card = `rounded-xl border shadow-sm ${
    darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
  }`;

  const loadAll = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [windows, requests, itin, userReq] = await Promise.all([
      fetchVisitWindows(type),
      fetchVisitSeatRequests(type),
      fetchVisitItinerary(type),
      fetchUserVisitRequest(type, currentUser.uid),
    ]);
    setItinerary(itin);
    setUserRequest(userReq);
    setAvailableWindows(computeAvailableWindows(windows, requests, { excludeUid: currentUser.uid }));
    setLoading(false);
  }, [currentUser, type]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Pull the requester's account (company + name) so the CRM side can label it.
  useEffect(() => {
    if (!currentUser?.email) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getMyAccountSnapshot();
        if (cancelled || snap.empty) return;
        const a = snap.docs[0].data();
        setAccount({
          company: a.company || '',
          name: `${a.contact?.firstName || ''} ${a.contact?.lastName || ''}`.trim(),
        });
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const handleSubmit = async () => {
    if (!currentUser || !selectedWindowId) return;
    const win = availableWindows.find((w) => w.id === selectedWindowId);
    if (!win) return;
    setSubmitting(true);
    try {
      const payload = {
        userEmail: currentUser.email,
        companyName: account?.company || '',
        requestedByName: account?.name || currentUser.displayName || '',
        windowId: win.id,
        day1: win.day1,
        day2: win.day2,
        status: 'requested',
      };
      if (userRequest) {
        await updateVisitRequest(type, currentUser.uid, payload);
      } else {
        await submitVisitRequest(type, currentUser.uid, payload);
      }
      // Notify general fulfillment.
      try {
        const { triggerFulfillmentNotification } = await import('../services/notificationTriggerService');
        await triggerFulfillmentNotification('general', {
          type: type === 'cqc' ? 'CQC visit' : 'on-site visit',
          name: `${title} Request`,
          description: `${title} request for ${formatWindowRange(win.day1, win.day2)}`,
          requestedBy: account?.name || currentUser.displayName || currentUser.email,
          company: account?.company || '',
          day1: win.day1,
          day2: win.day2,
          timestamp: new Date().toISOString(),
        });
      } catch {
        /* notification is best-effort */
      }
      setShowConfirm(false);
      setEditing(false);
      setSelectedWindowId('');
      await loadAll();
    } catch (e) {
      console.error('submit visit request error:', e);
      alert('Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!currentUser) return;
    if (!window.confirm(`Cancel your ${title.toLowerCase()} request?`)) return;
    try {
      await deleteVisitRequest(type, currentUser.uid);
      setUserRequest(null);
      await loadAll();
    } catch (e) {
      console.error('cancel request error:', e);
      alert('Could not cancel the request.');
    }
  };

  // ---- Loading / auth gates ----
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className={`h-8 w-8 animate-spin ${accentClasses.text}`} />
      </div>
    );
  }
  if (!currentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center text-gray-900 dark:text-white">
          <h2 className="mb-2 text-2xl font-bold">Authentication Required</h2>
          <p className="text-gray-500 dark:text-gray-400">Please log in to request a {title.toLowerCase()}.</p>
        </div>
      </div>
    );
  }

  const status = userRequest?.status;
  const showPicker = !userRequest || editing || status === 'denied';

  return (
    <div className="relative min-h-screen">
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div className="border-b px-6 py-6 border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80">
        <div className="relative mx-auto flex max-w-5xl items-center justify-center">
          <button
            onClick={() => window.history.back()}
            className={`absolute left-0 flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${
              darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">{title}</h1>
            <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">{subtitle}</p>
            {location && (
              <div className="mt-2 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <MapPin className="h-4 w-4" />
                <span>{location}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl space-y-6 p-6">
        {/* What to Expect + videos */}
        <section className={`${card} p-6 sm:p-8`}>
          <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">{overviewTitle}</h2>
          {overviewCards.length > 0 && (
            <div className="mb-8 grid gap-6 sm:grid-cols-3">
              {overviewCards.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div key={i} className="text-center">
                    <div className={`mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl ${accentClasses.tile}`}>
                      {Icon && <Icon className="h-6 w-6" />}
                    </div>
                    <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">{c.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{c.text}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Two client videos */}
          <div className="grid gap-6 sm:grid-cols-2">
            {videos.map((v, i) => (
              <div key={i}>
                <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-100 dark:border-gray-800 dark:bg-gray-800" style={{ paddingTop: '56.25%' }}>
                  {v.url ? (
                    <iframe
                      src={v.url}
                      title={v.caption || `Client video ${i + 1}`}
                      className="absolute inset-0 h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                      <Video className="mb-2 h-10 w-10" />
                      <span className="text-sm">Video coming soon</span>
                    </div>
                  )}
                </div>
                {v.caption && <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">{v.caption}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* Two-day itinerary */}
        <section className={`${card} p-6 sm:p-8`}>
          <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">2-Day Itinerary</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {itinerary.map((day, idx) => (
              <div key={idx} className={`rounded-xl border p-5 ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                <div className="mb-4 flex items-center gap-3">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold text-white ${accentClasses.chip}`}>
                    {day.day || idx + 1}
                  </span>
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-400">Day {day.day || idx + 1}</div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{day.title}</h3>
                  </div>
                </div>
                <ul className="space-y-3">
                  {(day.blocks || []).map((b, bi) => (
                    <li key={bi} className="flex gap-3">
                      <div className={`flex-shrink-0 text-sm font-semibold tabular-nums ${accentClasses.text}`} style={{ width: '4.5rem' }}>
                        {b.time}
                      </div>
                      <div className="flex-1 text-sm text-gray-600 dark:text-gray-300">{b.activity}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Current request status */}
        {userRequest && status !== 'denied' && (
          <section className={`${card} p-6 sm:p-8`}>
            <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">Your {title} Request</h2>
            <div className="mx-auto max-w-md text-center">
              <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${accentClasses.tile}`}>
                {status === 'completed' ? <Check className="h-7 w-7" /> : status === 'scheduled' ? <CalendarCheck className="h-7 w-7" /> : <Clock className="h-7 w-7" />}
              </div>
              <StatusBadge status={status} />
              <div className="mt-4 space-y-1 text-gray-600 dark:text-gray-300">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatWindowRange(userRequest.day1, userRequest.day2)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {status === 'requested' && 'Awaiting confirmation from the BBA team.'}
                  {status === 'scheduled' && 'Your visit is confirmed — see you then!'}
                  {status === 'completed' && 'Thanks for visiting — we hope it was valuable.'}
                </p>
              </div>
              {(status === 'requested') && (
                <div className="mt-6 flex justify-center gap-3">
                  <button
                    onClick={() => {
                      setEditing(true);
                      setSelectedWindowId('');
                    }}
                    className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${
                      darkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Pencil className="h-4 w-4" /> Change dates
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30"
                  >
                    <X className="h-4 w-4" /> Cancel
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Window picker */}
        {showPicker && (
          <section className={`${card} p-6 sm:p-8`}>
            <h2 className="mb-2 text-center text-2xl font-bold text-gray-900 dark:text-white">
              {editing ? 'Choose new dates' : `Request Your ${title}`}
            </h2>
            <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {status === 'denied' && 'Your previous request wasn’t available. '}
              Select from our open 2-day windows below.
            </p>

            {availableWindows.length === 0 ? (
              <div className="rounded-xl border border-dashed p-10 text-center dark:border-gray-700">
                <Calendar className="mx-auto mb-3 h-10 w-10 text-gray-400" />
                <p className="font-medium text-gray-900 dark:text-white">No open dates right now</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Please check back soon — new windows are added regularly.
                </p>
              </div>
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {availableWindows.map((w) => {
                    const selected = selectedWindowId === w.id;
                    return (
                      <button
                        key={w.id}
                        onClick={() => setSelectedWindowId(w.id)}
                        className={`rounded-xl border p-4 text-left transition-all ${
                          selected
                            ? `${accentClasses.ring} ring-2`
                            : darkMode
                              ? 'border-gray-800 hover:border-gray-700'
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Calendar className={`h-4 w-4 ${accentClasses.text}`} />
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {formatWindowRange(w.day1, w.day2)}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {w.seatsLeft} {w.seatsLeft === 1 ? 'spot' : 'spots'} left
                        </p>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-6 flex justify-center gap-3">
                  {editing && (
                    <button
                      onClick={() => {
                        setEditing(false);
                        setSelectedWindowId('');
                      }}
                      className={`rounded-lg border px-6 py-3 text-sm font-medium ${
                        darkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={() => setShowConfirm(true)}
                    disabled={!selectedWindowId}
                    className={`rounded-lg px-8 py-3 font-semibold text-white transition-colors ${accentClasses.solid} ${
                      selectedWindowId ? '' : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    {editing ? 'Update Request' : `Request ${title}`}
                  </button>
                </div>
              </>
            )}
          </section>
        )}
      </div>

      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-xl shadow-xl ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className="p-6">
              <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${accentClasses.tile}`}>
                <CalendarCheck className="h-6 w-6" />
              </div>
              <h3 className="mb-2 text-center text-xl font-bold text-gray-900 dark:text-white">Confirm your request</h3>
              <p className="mb-4 text-center text-gray-600 dark:text-gray-300">
                {title} on
                <br />
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatWindowRange(
                    availableWindows.find((w) => w.id === selectedWindowId)?.day1,
                    availableWindows.find((w) => w.id === selectedWindowId)?.day2
                  )}
                </span>
              </p>
              <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
                The BBA team will confirm your dates shortly.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={submitting}
                  className={`flex-1 rounded-lg border px-4 py-2.5 font-medium ${
                    darkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className={`flex-1 rounded-lg px-4 py-2.5 font-semibold text-white ${accentClasses.solid} disabled:opacity-60`}
                >
                  {submitting ? 'Submitting…' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

function StatusBadge({ status }) {
  const map = {
    requested: { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
    scheduled: { label: 'Scheduled', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' },
    completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  };
  const c = map[status] || map.requested;
  return <span className={`inline-block rounded-full px-3 py-1 text-sm font-medium ${c.cls}`}>{c.label}</span>;
}

export default VisitBookingPage;
